// /Users/pramod/Desktop/school-erp/frontend/src/Head-Of-Institution/LiveMonitoring/components/ClassStatusTable.jsx

import React from 'react';
import { Clock, AlertCircle, User, MapPin, Users as UsersIcon, GraduationCap } from 'lucide-react';

const ClassStatusTable = ({ classStatus }) => {
  const getStatusBadge = (hasLive, hasUpcoming) => {
    if (hasLive) {
      return { text: 'Live Now', color: 'bg-green-50 text-green-700 border-green-200', icon: <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span> };
    } else if (hasUpcoming) {
      return { text: 'Up Next', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: <Clock className="w-3 h-3 text-blue-500" /> };
    } else {
      return { text: 'Idle', color: 'bg-gray-50 text-gray-500 border-gray-200', icon: <AlertCircle className="w-3 h-3 text-gray-400" /> };
    }
  };

  const getFacultyName = (session) => {
    if (session?.faculty?.name) return session.faculty.name;
    if (session?.facultyName) return session.facultyName;
    return null;
  };

  const getSubjectName = (session) => {
    if (session?.subject?.name) return session.subject.name;
    if (session?.subjectName) return session.subjectName;
    return null;
  };

  const getGroupDetails = (session) => {
    if (session?.electiveGroup?.subjects) {
      const totalFaculties = session.electiveGroup.subjects.reduce(
        (acc, s) => acc + (s.subGroups?.length || 0), 0
      );
      const subjectNames = session.electiveGroup.subjects.map(s => s.subjectName).filter(Boolean);
      return { totalFaculties, subjectNames };
    }
    return null;
  };

  const getAcademicContextDisplay = (academicContext) => {
    if (!academicContext) return null;
    
    if (academicContext.isSemesterBased) {
      const semesterName = academicContext.semesterName || `Semester ${academicContext.semesterNumber || ''}`;
      const academicYearName = academicContext.academicYearName || 'Academic Year';
      
      return {
        type: 'semester',
        display: semesterName,
        year: academicYearName,
        fullDisplay: `${academicYearName} • ${semesterName}`
      };
    } else {
      const termName = academicContext.termName || `Term ${academicContext.termNumber || ''}`;
      const academicYearName = academicContext.academicYearName || 'Academic Year';
      
      return {
        type: 'term',
        display: termName,
        year: academicYearName,
        fullDisplay: `${academicYearName} • ${termName}`
      };
    }
  };

  const getAcademicContext = (item) => {
    if (item.academicContext) {
      return {
        isSemesterBased: item.academicContext.isSemesterBased || false,
        semesterName: item.academicContext.semesterName || null,
        semesterNumber: item.academicContext.semesterNumber || null,
        termName: item.academicContext.termName || null,
        termNumber: item.academicContext.termNumber || null,
        academicYearName: item.academicContext.academicYearName || null,
        semesterId: item.academicContext.semesterId || null,
        termId: item.academicContext.termId || null,
        academicYearId: item.academicContext.academicYearId || null
      };
    }
    return null;
  };

  return (
    <div className="w-full">
      {/* ===== MOBILE CARDS LAYOUT (Visible below md breakpoint) ===== */}
      <div className="grid grid-cols-1 gap-3 md:hidden">
        {classStatus.map((item, index) => {
          const status = getStatusBadge(item.hasLiveSession, item.hasUpcomingSession);
          const currentSession = item.currentSession || item.upcomingSession;
          const facultyName = getFacultyName(currentSession);
          const subjectName = getSubjectName(currentSession);
          const isGroupSession = currentSession?.isElectiveSession && currentSession?.electiveGroup;
          const groupDetails = getGroupDetails(currentSession);
          
          const groupName = currentSession?.electiveGroup?.name || 
                           currentSession?.electiveGroupName || 
                           (isGroupSession ? 'Group' : null);
          
          const academicCtx = getAcademicContext(item);
          const academicDisplay = academicCtx ? getAcademicContextDisplay(academicCtx) : null;

          return (
            <div key={index} className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3 shadow-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className="text-[10px] font-bold text-gray-400 font-mono block">ROOM #{index + 1}</span>
                  <h4 className="text-sm font-bold text-gray-900 truncate">{item.className}</h4>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${status.color}`}>
                  {status.icon}
                  {status.text}
                </span>
              </div>

              {currentSession ? (
                <div className="p-3 bg-gray-50 rounded-xl space-y-2 border border-gray-100 text-xs">
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 block uppercase tracking-wide">Lesson Details</span>
                    <p className="font-bold text-gray-800 flex items-center gap-1.5 flex-wrap mt-0.5">
                      {isGroupSession ? (groupName || 'Group') : (subjectName || currentSession.type || 'Session')}
                      {currentSession.isOverride && <span className="text-amber-600 font-medium bg-amber-50 px-1 rounded border border-amber-200 text-[10px]">Adjusted</span>}
                      {isGroupSession && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.2 rounded border border-emerald-200">Group</span>}
                    </p>
                    {isGroupSession && groupDetails && (
                      <p className="text-gray-500 font-medium text-[11px] mt-0.5 leading-relaxed">
                        {groupDetails.subjectNames.join(', ')} <span className="text-gray-400">({groupDetails.totalFaculties} teachers)</span>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1.5 border-t border-gray-200/60 text-gray-600 font-semibold">
                    <div className="flex items-center gap-1 min-w-0">
                      <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="font-mono truncate">{currentSession.timeSlot?.start} - {currentSession.timeSlot?.end}</span>
                    </div>
                    
                    <div className="flex items-center gap-1 min-w-0">
                      {isGroupSession ? (
                        <>
                          <UsersIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                          <span className="truncate">Multiple Instructors</span>
                        </>
                      ) : facultyName ? (
                        <>
                          <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          <span className="truncate">{facultyName}</span>
                        </>
                      ) : (
                        <span className="text-gray-400 font-normal">-</span>
                      )}
                    </div>
                  </div>

                  {currentSession.room && (
                    <div className="pt-1.5 border-t border-gray-200/60 flex items-center gap-1 text-gray-600 font-semibold">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span>Location/Area: <strong className="font-mono text-gray-800">{currentSession.room}</strong></span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic bg-gray-50/50 p-2.5 rounded-xl border border-dashed border-gray-200 text-center">
                  No active lessons mapped at this moment
                </div>
              )}

              {academicDisplay && (
                <div className="flex items-start gap-1.5 text-[11px] text-gray-500 bg-indigo-50/40 px-3 py-2 rounded-xl border border-indigo-100/50 font-medium">
                  <GraduationCap className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-gray-700 font-bold block truncate">{academicDisplay.year}</span>
                    <span className="text-gray-400 block mt-0.5">
                      {academicDisplay.type === 'semester' ? `Semester: ${academicDisplay.display}` : `Term: ${academicDisplay.display}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ===== DESKTOP STANDARD LAYOUT TABLE (Visible on md screens and up) ===== */}
      <div className="hidden md:block overflow-x-auto border border-gray-200 rounded-2xl bg-white shadow-xs">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-400 uppercase tracking-wider select-none">
              <th className="px-4 py-3.5 w-12 text-center">#</th>
              <th className="px-4 py-3.5">Classroom</th>
              <th className="px-4 py-3.5 w-32">Status</th>
              <th className="px-4 py-3.5">Assigned Session</th>
              <th className="px-4 py-3.5">Teacher</th>
              <th className="px-4 py-3.5 w-28">Location</th>
              <th className="px-4 py-3.5">Academic Framework</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white text-sm">
            {classStatus.map((item, index) => {
              const status = getStatusBadge(item.hasLiveSession, item.hasUpcomingSession);
              const currentSession = item.currentSession || item.upcomingSession;
              const facultyName = getFacultyName(currentSession);
              const subjectName = getSubjectName(currentSession);
              const isGroupSession = currentSession?.isElectiveSession && currentSession?.electiveGroup;
              const groupDetails = getGroupDetails(currentSession);
              
              const groupName = currentSession?.electiveGroup?.name || 
                               currentSession?.electiveGroupName || 
                               (isGroupSession ? 'Group' : null);
              
              const academicCtx = getAcademicContext(item);
              const academicDisplay = academicCtx ? getAcademicContextDisplay(academicCtx) : null;
              
              return (
                <tr key={index} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3.5 text-center font-mono text-gray-400 font-medium">{index + 1}</td>
                  <td className="px-4 py-3.5 font-bold text-gray-800">{item.className}</td>
                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold border ${status.color}`}>
                      {status.icon}
                      {status.text}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    {currentSession ? (
                      <div className="space-y-0.5">
                        <p className="font-bold text-gray-800 flex items-center gap-1.5 flex-wrap">
                          {isGroupSession ? (groupName || 'Group') : (subjectName || currentSession.type || 'Session')}
                          {currentSession.isOverride && <span className="text-amber-700 bg-amber-50 px-1.5 rounded border border-amber-200 text-[10px] font-semibold">Adjusted</span>}
                          {isGroupSession && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.2 rounded border border-emerald-200">Group</span>}
                        </p>
                        {isGroupSession && groupDetails && (
                          <p className="text-xs text-gray-400 font-medium max-w-xs truncate" title={groupDetails.subjectNames.join(', ')}>
                            {groupDetails.subjectNames.join(', ')} <span className="text-[11px]">({groupDetails.totalFaculties} teachers)</span>
                          </p>
                        )}
                        <p className="text-xs text-gray-400 font-mono">{currentSession.timeSlot?.start} - {currentSession.timeSlot?.end}</p>
                      </div>
                    ) : (
                      <span className="text-gray-300 font-medium italic">- Idle -</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-gray-600">
                    {isGroupSession ? (
                      <div className="flex items-center gap-1.5 text-emerald-700">
                        <UsersIcon className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-xs font-bold">Multiple Views</span>
                      </div>
                    ) : facultyName ? (
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="truncate max-w-[150px]">{facultyName}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400 font-normal">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    {currentSession?.room ? (
                      <div className="flex items-center gap-1 text-gray-700 font-semibold">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <span className="font-mono">{currentSession.room}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5">
                    {academicDisplay ? (
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-xs font-bold text-gray-700">
                          <GraduationCap className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                          <span className="truncate max-w-[160px]">{academicDisplay.year}</span>
                        </div>
                        <p className="text-xs text-gray-400 font-medium pl-4.5">
                          {academicDisplay.type === 'semester' ? (
                            <span>Semester: <strong className="text-blue-600 font-bold">{academicDisplay.display}</strong></span>
                          ) : (
                            <span>Term: <strong className="text-purple-600 font-bold">{academicDisplay.display}</strong></span>
                          )}
                        </p>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClassStatusTable;